/**
 * API Response Caching Utilities
 * Provides in-memory and localStorage caching for API responses
 */

// Cache configuration
const CACHE_CONFIG = {
  DEFAULT_TTL: 5 * 60 * 1000, // 5 minutes
  MAX_ENTRIES: 100,
  STORAGE_PREFIX: 'campConnect_cache_'
};

// In-memory cache
const memoryCache = new Map();

/**
 * Generate cache key from parameters
 * @param {string} baseKey - Base cache key
 * @param {Object} params - Parameters to include in key
 * @returns {string} - Generated cache key
 */
export const generateCacheKey = (baseKey, params = {}) => {
  const paramString = Object.keys(params)
    .sort()
    .map(key => `${key}=${JSON.stringify(params[key])}`)
    .join('&');
  
  return paramString ? `${baseKey}?${paramString}` : baseKey;
};

/**
 * Cache entry structure
 */
class CacheEntry {
  constructor(data, ttl = CACHE_CONFIG.DEFAULT_TTL) {
    this.data = data;
    this.timestamp = Date.now();
    this.ttl = ttl;
    this.expiresAt = this.timestamp + ttl;
  }

  isExpired() {
    return Date.now() > this.expiresAt;
  }

  getData() {
    return this.isExpired() ? null : this.data;
  }
}

/**
 * Get data from memory cache
 * @param {string} key - Cache key
 * @returns {any|null} - Cached data or null
 */
export const getFromMemoryCache = (key) => {
  const entry = memoryCache.get(key);
  if (!entry) return null;
  
  const data = entry.getData();
  if (data === null) {
    memoryCache.delete(key);
  }
  
  return data;
};

/**
 * Set data in memory cache
 * @param {string} key - Cache key
 * @param {any} data - Data to cache
 * @param {number} ttl - Time to live in milliseconds
 */
export const setInMemoryCache = (key, data, ttl = CACHE_CONFIG.DEFAULT_TTL) => {
  // Clean up expired entries if cache is getting large
  if (memoryCache.size >= CACHE_CONFIG.MAX_ENTRIES) {
    cleanupMemoryCache();
  }
  
  memoryCache.set(key, new CacheEntry(data, ttl));
};

/**
 * Clean up expired entries from memory cache
 */
export const cleanupMemoryCache = () => {
  for (const [key, entry] of memoryCache.entries()) {
    if (entry.isExpired()) {
      memoryCache.delete(key);
    }
  }
};

/**
 * Get data from localStorage cache
 * @param {string} key - Cache key
 * @returns {any|null} - Cached data or null
 */
export const getFromStorageCache = (key) => {
  try {
    const storageKey = `${CACHE_CONFIG.STORAGE_PREFIX}${key}`;
    const cached = localStorage.getItem(storageKey);
    
    if (!cached) return null;
    
    const entry = JSON.parse(cached);
    const cacheEntry = new CacheEntry(entry.data, entry.ttl);
    cacheEntry.timestamp = entry.timestamp;
    cacheEntry.expiresAt = entry.expiresAt;
    
    const data = cacheEntry.getData();
    if (data === null) {
      localStorage.removeItem(storageKey);
    }
    
    return data;
  } catch (error) {
    console.warn('Error reading from storage cache:', error);
    return null;
  }
};

/**
 * Set data in localStorage cache
 * @param {string} key - Cache key
 * @param {any} data - Data to cache
 * @param {number} ttl - Time to live in milliseconds
 */
export const setInStorageCache = (key, data, ttl = CACHE_CONFIG.DEFAULT_TTL) => {
  try {
    const storageKey = `${CACHE_CONFIG.STORAGE_PREFIX}${key}`;
    const entry = new CacheEntry(data, ttl);
    
    localStorage.setItem(storageKey, JSON.stringify({
      data: entry.data,
      timestamp: entry.timestamp,
      ttl: entry.ttl,
      expiresAt: entry.expiresAt
    }));
  } catch (error) {
    console.warn('Error writing to storage cache:', error);
  }
};

/**
 * Get data from cache (memory first, then storage)
 * @param {string} key - Cache key
 * @returns {any|null} - Cached data or null
 */
export const getFromCache = (key) => {
  // Try memory cache first
  let data = getFromMemoryCache(key);
  if (data !== null) return data;
  
  // Try storage cache
  data = getFromStorageCache(key);
  if (data !== null) {
    // Store in memory cache for faster access
    setInMemoryCache(key, data);
    return data;
  }
  
  return null;
};

/**
 * Set data in cache (both memory and storage)
 * @param {string} key - Cache key
 * @param {any} data - Data to cache
 * @param {number} ttl - Time to live in milliseconds
 */
export const setInCache = (key, data, ttl = CACHE_CONFIG.DEFAULT_TTL) => {
  setInMemoryCache(key, data, ttl);
  setInStorageCache(key, data, ttl);
};

/**
 * Remove data from cache
 * @param {string} key - Cache key
 */
export const removeFromCache = (key) => {
  memoryCache.delete(key);
  try {
    const storageKey = `${CACHE_CONFIG.STORAGE_PREFIX}${key}`;
    localStorage.removeItem(storageKey);
  } catch (error) {
    console.warn('Error removing from storage cache:', error);
  }
};

/**
 * Clear all cache data
 */
export const clearCache = () => {
  memoryCache.clear();
  
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(CACHE_CONFIG.STORAGE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn('Error clearing storage cache:', error);
  }
};

/**
 * Cached API call wrapper
 * @param {string} cacheKey - Cache key
 * @param {Function} apiCall - API call function
 * @param {number} ttl - Cache TTL in milliseconds
 * @param {boolean} useStorage - Whether to use localStorage cache
 * @returns {Promise<any>} - API response (from cache or API)
 */
export const cachedAPICall = async (cacheKey, apiCall, ttl = CACHE_CONFIG.DEFAULT_TTL, useStorage = true) => {
  // Try to get from cache
  const cachedData = getFromCache(cacheKey);
  if (cachedData !== null) {
    console.log(`Cache hit for ${cacheKey}`);
    return cachedData;
  }
  
  console.log(`Cache miss for ${cacheKey}, calling API`);
  
  try {
    // Call API
    const data = await apiCall();
    
    // Store in cache
    if (useStorage) {
      setInCache(cacheKey, data, ttl);
    } else {
      setInMemoryCache(cacheKey, data, ttl);
    }
    
    return data;
  } catch (error) {
    console.error(`API call failed for ${cacheKey}:`, error);
    throw error;
  }
};

/**
 * Cache TTL constants
 */
export const CACHE_TTL = {
  SHORT: 1 * 60 * 1000,      // 1 minute
  MEDIUM: 5 * 60 * 1000,     // 5 minutes
  LONG: 30 * 60 * 1000,      // 30 minutes
  VERY_LONG: 24 * 60 * 60 * 1000, // 24 hours
  PERMANENT: Infinity         // Never expires
};

/**
 * Cache keys for different data types
 */
export const CACHE_KEYS = {
  USER_LOCATION: 'user_location',
  WEATHER_DATA: 'weather_data',
  PLACES_SEARCH: 'places_search',
  GEOCODING: 'geocoding',
  USER_PROFILE: 'user_profile',
  FRIENDS_LIST: 'friends_list',
  CAMPS_DATA: 'camps_data'
};

// Clean up expired entries periodically
setInterval(cleanupMemoryCache, 5 * 60 * 1000); // Every 5 minutes
